import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../utils/api';
import { Client, Server, Ticket, Announcement, AccountingItem, AccountingCycle } from '../types';

interface DataContextType {
  clients: Client[];
  servers: Server[];
  tickets: Ticket[];
  announcements: Announcement[];
  clientServers: Server[];
  clientTickets: Ticket[];
  logs: any[];
  cycles: AccountingCycle[];
  allMovements: AccountingItem[];
  loading: boolean;
  error: string | null;
  refreshData: (options?: { setLoading?: boolean }) => Promise<void>; // Modificado para aceptar options
  startNewCycle: () => Promise<any>;
  closeCycle: (cycleId: number, data: any) => Promise<any>;
  createTicket: (ticketData: any) => Promise<any>;
  addTicketReply: (ticketId: string, replyData: any) => Promise<any>;
  updateTicketStatus: (ticketId: string, status: string) => Promise<any>;
  updateServer: (serverId: string, serverData: Partial<Server>) => Promise<any>;
  assignServer: (serverId: string, clientIds: string[]) => Promise<any>;
  toggleServerLock: (serverId: string) => Promise<any>;
  renewServer: (serverId: string) => Promise<any>;
  deleteServer: (serverId: string) => Promise<void>;
  addServer: (serverData: Omit<Server, "id" | "status">) => Promise<void>;
  addClient: (clientData: Omit<Client, "id" | "hash" | "createdAt">) => Promise<void>;
  deleteClient: (clientId: string) => Promise<void>;
  saveClientTelegramId: (clientId: string, chatId: string) => Promise<void>;
  updateClientStatus: (clientId: string, status: 'active' | 'banned') => Promise<void>;
  syncRoyaleInvoices: () => Promise<any>;
  syncMercury: (daysBack?: number) => Promise<any>;
  addExpense: (expenseData: any) => Promise<any>;
  addTransaction: (transactionData: any) => Promise<any>;
  deleteMovement: (type: 'income' | 'expense', id: string | number) => Promise<any>;
  updateMovement: (type: 'income' | 'expense', id: string | number, data: AccountingItem) => Promise<any>;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [appData, setAppData] = useState({
    clients: [] as Client[],
    servers: [] as Server[],
    tickets: [] as Ticket[],
    announcements: [] as Announcement[],
    clientServers: [] as Server[],
    clientTickets: [] as Ticket[],
    logs: [] as any[],
    cycles: [] as AccountingCycle[],
    expenses: [] as AccountingItem[],
    transactions: [] as AccountingItem[],
  });

  const fetchData = useCallback(async (options?: { setLoading?: boolean }) => {
    if (!isAuthenticated || !user) {
        setLoading(false);
        setAppData({ // Limpiar datos si no está autenticado
          clients: [], servers: [], tickets: [], announcements: [],
          clientServers: [], clientTickets: [], logs: [], cycles: [],
          expenses: [], transactions: []
      });
      setError(null);
        return;
    }

    if (options?.setLoading !== false) {
        setLoading(true);
    }
    setError(null);

    try {
      if (user.role === 'admin') {
        const [
          clientsData, serversData, ticketsData,
          announcementsData, logsData, rawExpenses,
          rawTransactions, cyclesData
        ] = await Promise.all([
          api.getClients(),
          api.getServers(),
          api.getTickets(),
          api.getAllAnnouncements(),
          api.getLogs(),
          api.getAllExpenses(),
          api.getAllTransactions(),
          api.getAccountingCycles(),
        ]).catch(err => { // Captura de error para Promise.all
            console.error("Error en Promise.all (admin):", err);
            throw new Error(`Fallo al cargar datos iniciales: ${err.message}`);
        });

        const expenses = (rawExpenses || []).map((item: any) => ({
          ...item, id: `exp-${item.id}`, amount: parseFloat(item.amount),
          date: new Date(item.expenseDate), type: 'expense' as const, source: item.vendor
        }));

        const clientsMap = new Map((clientsData || []).map((c: Client) => [c.id, c.name]));
        const transactions = (rawTransactions || []).map((item: any) => ({
          ...item, id: `inc-${item.id}`, amount: parseFloat(item.amount),
          date: new Date(item.transactionDate), type: 'income' as const, source: clientsMap.get(item.clientId) || 'Cliente Desconocido'
        }));

        setAppData({
          clients: clientsData || [],
          servers: serversData || [],
          tickets: ticketsData || [],
          logs: logsData || [],
          announcements: announcementsData || [],
          cycles: cyclesData || [],
          expenses: expenses,
          transactions: transactions,
          clientServers: [],
          clientTickets: [],
        });
      } else if (user.role === 'client') {
        const clientId = user.id;

        if (!clientId) {
          throw new Error("No se pudo encontrar el ID del cliente para cargar sus datos.");
        }

        const [
          clientServersData,
          clientTicketsData,
          announcementsData
        ] = await Promise.all([
            api.getServersForClient(clientId),
            api.getClientTickets(clientId),
            api.getAnnouncements()
        ]).catch(err => { // Captura de error para Promise.all
            console.error("Error en Promise.all (client):", err);
            throw new Error(`Fallo al cargar datos del cliente: ${err.message}`);
        });

        setAppData(prevData => ({
            ...prevData,
            clientServers: clientServersData || [],
            clientTickets: clientTicketsData || [],
            announcements: announcementsData || [],
            clients: [], servers: [], tickets: [], logs: [], cycles: [], expenses: [], transactions: [] // Limpiar datos de admin
        }));
      }
    } catch (err: any) {
      console.error("Error al cargar los datos (fetchData):", err);
      setError(`No se pudieron cargar los datos: ${err.message}`);
     // Limpiar datos en caso de error para evitar mostrar datos incorrectos
      setAppData({
          clients: [], servers: [], tickets: [], announcements: [],
          clientServers: [], clientTickets: [], logs: [], cycles: [],
          expenses: [], transactions: []
      });
    } finally {
      setLoading(false);
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const allMovements = useMemo(() => {
    // Asegurarse de que date es un objeto Date válido antes de comparar
    const safeSort = (a: AccountingItem, b: AccountingItem) => {
        const dateA = a.date instanceof Date ? a.date.getTime() : 0;
        const dateB = b.date instanceof Date ? b.date.getTime() : 0;
        return dateB - dateA;
    };
    return [...appData.expenses, ...appData.transactions].sort(safeSort);
  }, [appData.expenses, appData.transactions]);

  // --- 👇 ESTA ES LA FUNCIÓN CORREGIDA ---
  const createApiFunction = <T extends (...args: any[]) => Promise<any>>(apiCall: T) =>
    async (...callArgs: Parameters<T>): Promise<ReturnType<T>> => {
        // Obtenemos las options del último argumento si existen
        const lastArg = callArgs[callArgs.length - 1];
        const options = (typeof lastArg === 'object' && lastArg !== null && 'setLoading' in lastArg) ? lastArg : undefined;
        const actualArgs = options ? callArgs.slice(0, -1) : callArgs;

        try {
            const result = await apiCall(...actualArgs); // <-- Usamos spread (...) para pasar TODOS los argumentos
            // Solo refresca si la llamada API fue exitosa
             await fetchData(options); // <-- Pasamos las options a fetchData
             return result;
        } catch (error) {
            console.error("Error dentro de createApiFunction:", error);
            // Re-lanzamos el error para que el componente que llama pueda manejarlo (ej: mostrar toast)
            throw error;
        }
    };
  // --- FIN DE LA CORRECCIÓN ---

  const value: DataContextType = useMemo(() => ({
    ...appData,
    allMovements,
    loading,
    error,
    refreshData: fetchData, // Asignar la función directamente
    // Las funciones específicas ahora reciben los argumentos correctos
    startNewCycle: createApiFunction(api.startNewAccountingCycle),
    closeCycle: createApiFunction(api.closeAccountingCycle),
    createTicket: createApiFunction(api.createTicket),
    addTicketReply: createApiFunction(api.addTicketReply),
    updateTicketStatus: createApiFunction(api.updateTicketStatus),
    updateServer: createApiFunction(api.updateServer),
    assignServer: createApiFunction(api.assignServer), // Ahora pasará serverId y clientIds
    toggleServerLock: createApiFunction(api.toggleServerLock),
    renewServer: createApiFunction(api.renewServer),
    deleteServer: createApiFunction(api.deleteServer),
    addServer: createApiFunction(api.addServer),
    addClient: createApiFunction(api.addClient),
    deleteClient: createApiFunction(api.deleteClient),
    saveClientTelegramId: createApiFunction(api.saveClientTelegramId),
    updateClientStatus: createApiFunction(api.updateClientStatus),
    syncRoyaleInvoices: createApiFunction(api.syncRoyaleInvoices),
    syncMercury: createApiFunction(api.syncMercury),
    addExpense: createApiFunction(api.addExpense),
    addTransaction: createApiFunction(api.addTransaction),
    deleteMovement: createApiFunction(api.deleteMovement),
    updateMovement: createApiFunction(api.updateMovement),
  }), [appData, allMovements, loading, error, fetchData]); // Incluir fetchData en las dependencias

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData debe ser usado dentro de un DataProvider');
  }
  return context;
};
